import { Legend, PREVIOUS_INK } from './common';

export interface CompareRow {
  key: string;
  label: string;
  sublabel?: string;
  value: number;
  /** null = sem base no período anterior (sem traço). */
  previous: number | null;
}

/**
 * Barras horizontais, período atual x anterior (Faturamento por estação da
 * Tupi): a barra é o atual, numa cor só; o anterior é um traço de tinta na
 * mesma escala (estilo bullet) — um hue só, sem par de cores claro/escuro. Os
 * dois valores vão escritos na linha: nada depende de hover.
 */
export function CompareBars({
  rows,
  color,
  formatValue,
  ariaLabel
}: {
  rows: CompareRow[];
  color: string;
  formatValue: (v: number) => string;
  ariaLabel: string;
}) {
  if (rows.length === 0) {
    return <div className="py-8 text-center text-[13px] text-bow-royal/45">Nenhum carregador no período.</div>;
  }
  const max = Math.max(...rows.map((r) => Math.max(r.value, r.previous ?? 0)), 1e-9);
  const temAnterior = rows.some((r) => r.previous != null);

  return (
    <div role="group" aria-label={ariaLabel}>
      <ul className="m-0 flex list-none flex-col gap-3.5 p-0">
        {rows.map((r) => (
          <li key={r.key} className="text-[13px]">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate font-semibold text-bow-royal">
                {r.label}
                {r.sublabel && <span className="font-normal text-bow-royal/50"> · {r.sublabel}</span>}
              </span>
              <span className="shrink-0 tabular-nums">
                <span className="font-semibold text-bow-royal">{formatValue(r.value)}</span>
                {r.previous != null && (
                  <span className="text-bow-royal/50"> · antes {formatValue(r.previous)}</span>
                )}
              </span>
            </div>
            <div className="relative mt-1.5 h-3 rounded-full bg-bow-royal/[0.05]">
              {r.value > 0 && (
                <div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{ width: `${(100 * r.value) / max}%`, minWidth: 4, background: color }}
                />
              )}
              {r.previous != null && (
                <div
                  aria-hidden
                  className="absolute -top-1 h-5 w-[2px] rounded-full"
                  style={{ left: `calc(${(100 * r.previous) / max}% - 1px)`, background: PREVIOUS_INK }}
                />
              )}
            </div>
          </li>
        ))}
      </ul>
      {temAnterior && (
        <Legend
          items={[
            { label: 'Período atual', color },
            { label: 'Período anterior', color: PREVIOUS_INK, kind: 'line' }
          ]}
        />
      )}
    </div>
  );
}
